import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { useCartStatus } from "../hooks/useCart";
import { FiShoppingCart, FiLogOut, FiUser, FiCheckSquare, FiX, FiWifi, FiWifiOff, FiSearch, FiSidebar, FiArrowLeft } from "react-icons/fi";
import { FaQrcode } from "react-icons/fa";
import { useSidebar } from "../context/SidebarContext";
import { useCompanyLogo, useCompanyName } from "../hooks/useCompanySettings";
import HeaderCustomerSelector from "./HeaderCustomerSelector";
import HeaderFavorites from "./HeaderFavorites";
import UserSwitchModal from "./UserSwitchModal";
import routeRegistry from "../services/routeRegistry";
import useHeaderConfig from "../hooks/useHeaderConfig";
import GlobalSearchDropdown from "./GlobalSearchDropdown";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslation } from "../i18n";

const Header = ({ setSearchQuery, headerRef }) => {
  const { user, logout, userRoles, isSuperuser } = useContext(AuthContext);
  const { t } = useTranslation();
  const { toggleRightSidebar, rightVisible, toggleLeftSidebar, leftCollapsed } = useSidebar();
  const { config, hasHeader } = useHeaderConfig();

  const { itemCount, totalPrice, isEmpty } = useCartStatus();

  const { logo: companyLogo, loading: logoLoading } = useCompanyLogo();
  const { name: companyName } = useCompanyName();

  const [searchTerm, setSearchTerm] = useState("");
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showUserSwitchModal, setShowUserSwitchModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const searchTimeoutRef = useRef(null);
  const pendingRequestRef = useRef(null);

  const isOnProductsPage = location.pathname === '/products' || location.pathname === '/';

  const userRoleNames = Array.isArray(userRoles) ? userRoles : [];
  const effectiveRoles = isSuperuser ? [...userRoleNames, 'admin'] : userRoleNames;
  const hasCartAccess = user && routeRegistry.hasAccess('/cart', effectiveRoles);
  const hasCheckoutAccess = user && routeRegistry.hasAccess('/checkout', effectiveRoles);
  const hasProductCatalogAccess = user && routeRegistry.hasAccess('/', effectiveRoles);
  const hasProfileAccess = user && routeRegistry.hasAccess('/profile', effectiveRoles);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (pendingRequestRef.current) {
        pendingRequestRef.current.abort();
      }
    };
  }, []);

  const debouncedSearch = useCallback((value) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (pendingRequestRef.current) {
      pendingRequestRef.current.abort();
      pendingRequestRef.current = null;
    }
    if (value.trim().length > 0 && value.trim().length < 3) {
      return;
    }
    if (value.trim().length === 0) {
      setSearchQuery('');
      return;
    }
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value);
      searchTimeoutRef.current = null;
    }, 300);
  }, [setSearchQuery]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    const trimmedTerm = searchTerm.trim();
    if (trimmedTerm.length > 0 && trimmedTerm.length < 3) {
      return;
    }
    if (trimmedTerm) {
      if (isOnProductsPage) {
        setSearchQuery(trimmedTerm);
      } else {
        navigate(`/products?search=${encodeURIComponent(trimmedTerm)}`);
      }
    }
  };

  const clearSearch = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    setSearchTerm("");
    setSearchQuery("");
  };

  const handleLogout = () => {
    logout();
    setTimeout(() => navigate("/login"), 500);
  };

  if (!hasHeader) return null;

  const searchPlaceholder = config.search_placeholder ||
    (isOnProductsPage ? t("Search products...") : t("Search products (Enter to go to products page)"));

  return (
    <header ref={headerRef} className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
      <nav className="py-2">
        <div className="w-full flex items-center px-4">
          <div className="flex items-center">
            {config.show_left_sidebar_toggle && user && (
              <button
                className={`p-2 rounded-md transition-colors mr-4 ${
                  !leftCollapsed
                    ? 'text-primary bg-primary/10'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
                onClick={() => toggleLeftSidebar()}
                title={leftCollapsed ? t("Open Dashboard Menu") : t("Close Dashboard Menu")}
              >
                <FiSidebar className="h-5 w-5" />
              </button>
            )}

            {location.key !== 'default' && (
              <button
                className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors mr-2"
                onClick={() => navigate(-1)}
                title={t("Go Back")}
              >
                <FiArrowLeft className="h-5 w-5" />
              </button>
            )}

            {config.show_logo && (
              <Link className="mr-4" to="/">
                {logoLoading ? (
                  <div className="h-12 w-12" />
                ) : companyLogo && companyLogo.trim() !== '' ? (
                  <img
                    src={companyLogo}
                    alt={companyName || t("Company Logo")}
                    className="h-12 object-contain"
                  />
                ) : (
                  <div className="h-12" />
                )}
              </Link>
            )}
          </div>

          {config.show_favorites && config.favorites_position === 'in_header' && <HeaderFavorites />}

          {config.show_search ? (
            config.search_type === 'global' ? (
              <GlobalSearchDropdown
                placeholder={searchPlaceholder}
                onProductSearch={hasProductCatalogAccess ? setSearchQuery : null}
                className="flex-grow mr-4"
              />
            ) : hasProductCatalogAccess ? (
              <form onSubmit={handleSearchSubmit} className="flex-grow mr-4">
                <div className="flex">
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      className="px-3 py-2 border border-l-0 border-gray-200 bg-gray-100 hover:bg-gray-200 focus:outline-none"
                      onClick={clearSearch}
                      title={t("Clear search")}
                    >
                      <FiX />
                    </button>
                  )}
                  <button
                    type="submit"
                    className="px-3 py-2 border border-l-0 border-gray-200 bg-primary text-white rounded-r hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary"
                    title={isOnProductsPage ? t("Search products") : t("Search products (go to products page)")}
                  >
                    <FiSearch />
                  </button>
                </div>
              </form>
            ) : (
              <div className="flex-grow mr-4"></div>
            )
          ) : (
            <div className="flex-grow mr-4"></div>
          )}

          <div className="flex items-center space-x-2">
            {config.show_customer_selector && hasCheckoutAccess && <HeaderCustomerSelector />}

            <LanguageSwitcher />

            {config.show_network_status && (
              <span className="text-gray-500 text-sm mr-2">
                {isOnline ?
                  <FiWifi title={t("Online")} className="text-green-500" /> :
                  <FiWifiOff title={t("Offline")} className="text-yellow-500" />
                }
              </span>
            )}

            {config.show_cart && hasCartAccess && (
              <Link
                className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 bg-yellow-500 text-white hover:bg-yellow-600 focus:ring-yellow-500"
                to="/cart"
                title={t("Cart")}
              >
                <FiShoppingCart className="mr-1" />
                {itemCount > 0 && (
                  <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full ml-1">
                    {itemCount}
                  </span>
                )}
              </Link>
            )}

            {config.show_checkout && hasCheckoutAccess && !isEmpty && (
              <Link
                className="inline-flex items-center px-3 py-2 border border-green-500 text-sm font-medium rounded-md text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500"
                to="/checkout"
                title={t("Checkout - £{total}", { total: totalPrice })}
              >
                <FiCheckSquare className="mr-1" />
                <span className="hidden sm:inline">{t("Checkout")}</span>
                <span className="hidden sm:inline-flex items-center justify-center px-2 py-1 ml-1 text-xs font-medium text-white bg-green-600 rounded-full">
                  £{parseFloat(totalPrice).toFixed(2)}
                </span>
              </Link>
            )}

            {config.show_user_info && (
              <>
                {user ? (
                  <div className="flex items-center">
                    {hasProfileAccess && (
                      <Link
                        className="inline-flex items-center p-2 mr-1 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary"
                        to="/profile"
                        title={t("My Account")}
                        aria-label={t("My Account")}
                      >
                        <FiUser className="h-5 w-5" />
                      </Link>
                    )}
                    <button
                      onClick={() => setShowUserSwitchModal(true)}
                      className="hidden sm:flex items-center text-green-600 mr-2 hover:text-green-700 hover:underline cursor-pointer transition-all"
                      title={t("Click to switch user")}
                    >
                      <FaQrcode className="mr-1" /> {user.name}
                    </button>
                    <button
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      onClick={handleLogout}
                      title={t("Logout")}
                    >
                      <FiLogOut />
                    </button>
                  </div>
                ) : (
                  <Link
                    className="inline-flex items-center px-3 py-2 border border-blue-500 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    to="/login"
                    title={t("Login")}
                  >
                    <FiUser className="mr-1" />
                    <span>{t("Login")}</span>
                  </Link>
                )}
              </>
            )}
          </div>

          {config.show_right_sidebar_toggle && user && (
            <button
              className={`p-2 rounded-md transition-colors ml-2 ${
                rightVisible
                  ? 'text-primary bg-primary/10'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
              onClick={() => toggleRightSidebar()}
              title={rightVisible ? t("Close Quick Dashboard") : t("Open Quick Dashboard")}
            >
              <FiSidebar className="h-5 w-5 scale-x-[-1]" />
            </button>
          )}
        </div>
      </nav>

      {config.show_favorites && config.favorites_position === 'below_header' && (
        <div className="border-t border-gray-100 bg-white px-4 py-1 overflow-x-auto">
          <HeaderFavorites />
        </div>
      )}

      <UserSwitchModal
        show={showUserSwitchModal}
        onClose={() => setShowUserSwitchModal(false)}
      />
    </header>
  );
};

export default Header;
